import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Phone, PhoneIncoming, PhoneOff, PhoneForwarded,
  Mic, MicOff, Volume2, VolumeX, Pause, Play,
  Grid3X3, User, Clock, AlertCircle, CheckCircle,
  Loader2, X, PhoneCall, Ban
} from 'lucide-react';
import { agentMonitorAPI, dncAPI } from '../services/api';
import { useNotificationSound } from '../hooks/useNotificationSound';

// Call states
const CALL_STATES = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  RINGING: 'ringing',
  INCOMING: 'incoming',
  ACTIVE: 'active',
  ON_HOLD: 'on_hold',
  TRANSFERRING: 'transferring',
  ENDED: 'ended',
  FORM: 'form'  // New state for showing form after call
};

// Format duration
const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const AgentDialer = ({ userId, userName, onCallStart, onCallEnd }) => {
  // Notification sounds
  const { playSound, startRingtone, stopRingtone } = useNotificationSound();
  
  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  
  // Call state
  const [callState, setCallState] = useState(CALL_STATES.IDLE);
  const [incomingCall, setIncomingCall] = useState(null);
  const [currentCall, setCurrentCall] = useState(null);
  const [callerInfo, setCallerInfo] = useState(null);
  
  // Call controls
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  
  // Call duration
  const [callDuration, setCallDuration] = useState(0);
  const durationIntervalRef = useRef(null);
  
  // Transfer
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferNumber, setTransferNumber] = useState('');
  
  // Dialpad
  const [showDialpad, setShowDialpad] = useState(false);
  const [dialNumber, setDialNumber] = useState('');
  
  // Recent calls
  const [recentCalls, setRecentCalls] = useState([]);
  
  // DNC Quick Add state
  const [showDncConfirm, setShowDncConfirm] = useState(false);
  const [addingToDnc, setAddingToDnc] = useState(false);
  const [dncResult, setDncResult] = useState(null);
  
  // WebRTC refs
  const telnyxClientRef = useRef(null);
  const audioRef = useRef(null);

  // Simulate connection (in production, this would connect to Telnyx WebRTC)
  const connectToService = useCallback(async () => {
    setIsConnecting(true);
    setConnectionError(null);
    
    try {
      // In production, fetch WebRTC credentials and connect
      // For now, simulate connection
      await new Promise(resolve => setTimeout(resolve, 1500));
      setIsConnected(true);
      setIsConnecting(false);
      
      // Report agent login status
      try {
        await agentMonitorAPI.agentLogin(userId);
      } catch (e) {
        console.log('Agent status update failed:', e);
      }
    } catch (error) {
      setConnectionError('Failed to connect. Please try again.');
      setIsConnecting(false);
    }
  }, [userId]);

  // Connect on mount
  useEffect(() => {
    connectToService();
    
    return () => {
      // Cleanup
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      // Report agent logout on unmount
      agentMonitorAPI.agentLogout(userId).catch(() => {});
    };
  }, [connectToService, userId]);

  // Simulate incoming call (for demo purposes)
  useEffect(() => {
    if (!isConnected) return;
    
    // Simulate an incoming call after 10 seconds for demo
    const simulateIncoming = setTimeout(() => {
      if (callState === CALL_STATES.IDLE) {
        // Uncomment to enable simulated incoming calls
        // handleIncomingCall({
        //   callerId: '+1 (555) 123-4567',
        //   callerName: 'AI Voice Agent',
        //   reason: 'Fraud Alert Transfer'
        // });
      }
    }, 10000);
    
    return () => clearTimeout(simulateIncoming);
  }, [isConnected, callState]);

  // Handle incoming call
  const handleIncomingCall = (callData) => {
    setIncomingCall(callData);
    const incomingCallerInfo = {
      number: callData.callerId,
      name: callData.callerName || 'Unknown',
      reason: callData.reason || 'Transferred Call'
    };
    setCallerInfo(incomingCallerInfo);
    setCallState(CALL_STATES.INCOMING);
    
    // Play ringtone notification with caller info for desktop alert
    startRingtone(incomingCallerInfo);
  };

  // Answer incoming call
  const answerCall = () => {
    if (callState !== CALL_STATES.INCOMING) return;
    
    // Stop ringtone and play connected sound
    stopRingtone();
    playSound('callConnected');
    
    setCallState(CALL_STATES.ACTIVE);
    setCurrentCall(incomingCall);
    setIncomingCall(null);
    
    // Report call started to agent monitor
    const callNumber = incomingCall?.callerId || 'Unknown';
    agentMonitorAPI.callStarted(userId, incomingCall?.id || Date.now().toString(), callNumber)
      .catch(() => {});
    
    // Start duration timer
    setCallDuration(0);
    durationIntervalRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
    
    if (onCallStart) {
      onCallStart({ ...incomingCall, answeredAt: new Date() });
    }
  };

  // Reject incoming call
  const rejectCall = () => {
    stopRingtone();
    playSound('callEnded');
    setCallState(CALL_STATES.IDLE);
    setIncomingCall(null);
    setCallerInfo(null);
  };

  // End current call
  const endCall = () => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    
    // Play call ended sound
    playSound('callEnded');
    
    // Report call ended to agent monitor
    agentMonitorAPI.callEnded(userId, callDuration).catch(() => {});
    
    // Prepare call data for the form (passed to parent component)
    const callDataForForm = {
      id: Date.now(),
      ...currentCall,
      ...callerInfo,
      duration: callDuration,
      endedAt: new Date(),
      agentName: callerInfo?.name || 'AI Agent',
      transcript: `[Sample Transcript]\n\nAI Agent: Hello, this is the AI Fraud Detection system. I'm transferring a customer who reported suspicious activity on their account.\n\nCustomer: Yes, I noticed some charges I didn't make.\n\nAI Agent: I understand. I'm connecting you with a specialist who can help verify your identity and resolve this issue.\n\n[Call transferred to human agent at ${new Date().toLocaleTimeString()}]`,
      summary: `Call Summary:\n• Customer reported suspicious activity\n• Possible unauthorized charges detected\n• Customer needs identity verification\n• Account review recommended\n• Transferred from: ${callerInfo?.name || 'AI Fraud Detection'}\n• Reason: ${callerInfo?.reason || 'Suspicious Activity Transfer'}`
    };
    
    // Add to recent calls
    if (currentCall) {
      setRecentCalls(prev => [{
        ...callDataForForm
      }, ...prev.slice(0, 9)]);
    }
    
    if (onCallEnd) {
      onCallEnd({ duration: callDuration, call: currentCall, callData: callDataForForm });
    }
    
    setCallState(CALL_STATES.ENDED);
    setTimeout(() => {
      // Show completion message instead of embedded form
      setCallState(CALL_STATES.FORM);
      setCurrentCall(null);
      setCallDuration(0);
      setIsMuted(false);
      setIsOnHold(false);
    }, 2000);
  };

  // Handle form save - no longer needed as form is in parent component
  // const handleFormSave = async (formData) => { ... }

  // Close form and return to idle
  const closeForm = () => {
    setCallState(CALL_STATES.IDLE);
    setCallerInfo(null);
    setDncResult(null);
  };

  // DNC Quick Add handler
  const handleAddToDnc = async () => {
    const phoneToBlock = callerInfo?.phone || currentCall?.from || dialedNumber;
    
    if (!phoneToBlock) {
      setDncResult({ success: false, message: 'No phone number to block' });
      return;
    }
    
    setAddingToDnc(true);
    setDncResult(null);
    
    try {
      const response = await dncAPI.quickAdd(
        userId,
        phoneToBlock,
        'Customer requested - Do Not Call',
        currentCall?.id
      );
      
      if (response.data.success) {
        setDncResult({
          success: true,
          message: response.data.already_exists 
            ? 'Number is already on DNC list' 
            : 'Number added to Do Not Call list',
          phone: phoneToBlock
        });
      }
    } catch (error) {
      console.error('Error adding to DNC:', error);
      setDncResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to add to DNC list'
      });
    } finally {
      setAddingToDnc(false);
      setShowDncConfirm(false);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  // Toggle hold
  const toggleHold = () => {
    setIsOnHold(!isOnHold);
    setCallState(isOnHold ? CALL_STATES.ACTIVE : CALL_STATES.ON_HOLD);
  };

  // Transfer call
  const handleTransfer = () => {
    if (!transferNumber) return;
    
    setCallState(CALL_STATES.TRANSFERRING);
    
    // Simulate transfer
    setTimeout(() => {
      setShowTransfer(false);
      setTransferNumber('');
      endCall();
    }, 2000);
  };

  // Make outgoing call
  const makeCall = () => {
    if (!dialNumber || callState !== CALL_STATES.IDLE) return;
    
    setCallState(CALL_STATES.CONNECTING);
    setCallerInfo({
      number: dialNumber,
      name: 'Outgoing Call',
      reason: 'Manual Dial'
    });
    
    // Simulate connection
    setTimeout(() => {
      setCallState(CALL_STATES.ACTIVE);
      setCurrentCall({ callerId: dialNumber });
      setCallDuration(0);
      durationIntervalRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }, 2000);
    
    setShowDialpad(false);
    setDialNumber('');
  };

  // Dialpad button
  const DialButton = ({ digit, sub }) => (
    <button
      onClick={() => setDialNumber(prev => prev + digit)}
      className="w-16 h-16 rounded-full bg-[#1a1a1f] hover:bg-[#252530] border border-gray-700 flex flex-col items-center justify-center transition-colors"
    >
      <span className="text-white text-xl font-medium">{digit}</span>
      {sub && <span className="text-gray-500 text-[10px]">{sub}</span>}
    </button>
  );

  return (
    <div className="bg-[#0d1117] border border-gray-800 rounded-2xl overflow-hidden" data-testid="agent-dialer">
      {/* Hidden audio element for ringtone */}
      <audio ref={audioRef} loop>
        <source src="/ringtone.mp3" type="audio/mpeg" />
      </audio>

      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500/20 to-blue-500/20 border-b border-gray-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-400' : isConnecting ? 'bg-yellow-400 animate-pulse' : 'bg-red-400'}`} />
            <div>
              <h2 className="text-white font-semibold">Agent Dialer</h2>
              <p className="text-gray-400 text-sm">
                {isConnecting ? 'Connecting...' : isConnected ? 'Ready to receive calls' : 'Disconnected'}
              </p>
            </div>
          </div>
          {!isConnected && !isConnecting && (
            <button
              onClick={connectToService}
              className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Reconnect
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Incoming Call Alert */}
        {callState === CALL_STATES.INCOMING && callerInfo && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl animate-pulse">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                <PhoneIncoming className="w-8 h-8 text-green-400 animate-bounce" />
              </div>
              <div className="flex-1">
                <p className="text-green-400 font-semibold text-lg">Incoming Call</p>
                <p className="text-white text-xl font-bold">{callerInfo.name}</p>
                <p className="text-gray-400">{callerInfo.number}</p>
                <p className="text-teal-400 text-sm">{callerInfo.reason}</p>
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={answerCall}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors"
                data-testid="answer-call-btn"
              >
                <Phone className="w-5 h-5" />
                Answer
              </button>
              <button
                onClick={rejectCall}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold transition-colors"
                data-testid="reject-call-btn"
              >
                <PhoneOff className="w-5 h-5" />
                Decline
              </button>
            </div>
          </div>
        )}

        {/* Active Call */}
        {(callState === CALL_STATES.ACTIVE || callState === CALL_STATES.ON_HOLD || callState === CALL_STATES.TRANSFERRING) && (
          <div className="mb-6">
            {/* Call Info */}
            <div className="text-center mb-6">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-teal-500 to-blue-500 flex items-center justify-center mb-4">
                <User className="w-10 h-10 text-white" />
              </div>
              <p className="text-white text-xl font-bold">{callerInfo?.name || 'Unknown'}</p>
              <p className="text-gray-400">{callerInfo?.number}</p>
              <div className="flex items-center justify-center gap-2 mt-2">
                <Clock className="w-4 h-4 text-teal-400" />
                <span className="text-teal-400 font-mono text-lg">{formatDuration(callDuration)}</span>
              </div>
              {callState === CALL_STATES.ON_HOLD && (
                <span className="inline-block mt-2 px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm">
                  On Hold
                </span>
              )}
              {callState === CALL_STATES.TRANSFERRING && (
                <span className="inline-block mt-2 px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm">
                  Transferring...
                </span>
              )}
            </div>

            {/* Call Controls */}
            <div className="flex justify-center gap-4 mb-6">
              <button
                onClick={toggleMute}
                className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                  isMuted ? 'bg-red-500/20 text-red-400' : 'bg-gray-700 text-white hover:bg-gray-600'
                }`}
                title={isMuted ? 'Unmute' : 'Mute'}
                data-testid="mute-btn"
              >
                {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
              </button>
              
              <button
                onClick={toggleHold}
                className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                  isOnHold ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-700 text-white hover:bg-gray-600'
                }`}
                title={isOnHold ? 'Resume' : 'Hold'}
                data-testid="hold-btn"
              >
                {isOnHold ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
              </button>
              
              <button
                onClick={() => setShowTransfer(true)}
                className="w-14 h-14 rounded-full bg-gray-700 text-white hover:bg-gray-600 flex items-center justify-center transition-colors"
                title="Transfer"
                data-testid="transfer-btn"
              >
                <PhoneForwarded className="w-6 h-6" />
              </button>
              
              <button
                onClick={() => setShowDialpad(!showDialpad)}
                className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                  showDialpad ? 'bg-teal-500/20 text-teal-400' : 'bg-gray-700 text-white hover:bg-gray-600'
                }`}
                title="Keypad"
              >
                <Grid3X3 className="w-6 h-6" />
              </button>
            </div>

            {/* End Call Button + DNC Button */}
            <div className="flex justify-center gap-4">
              {/* Add to DNC Button */}
              <button
                onClick={() => setShowDncConfirm(true)}
                className="w-12 h-12 rounded-full bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 flex items-center justify-center transition-colors border border-orange-500/30"
                title="Add to Do Not Call list"
                data-testid="dnc-quick-add-btn"
              >
                <Ban className="w-5 h-5" />
              </button>
              
              {/* End Call Button */}
              <button
                onClick={endCall}
                className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
                data-testid="end-call-btn"
              >
                <PhoneOff className="w-8 h-8" />
              </button>
            </div>

            {/* DNC Result Message */}
            {dncResult && (
              <div className={`mt-4 p-3 rounded-lg text-center text-sm ${
                dncResult.success 
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                  : 'bg-red-500/20 text-red-400 border border-red-500/30'
              }`}>
                {dncResult.message}
              </div>
            )}
          </div>
        )}

        {/* Idle State - Show Dialpad */}
        {callState === CALL_STATES.IDLE && (
          <div>
            {/* Quick Actions */}
            <div className="flex gap-3 mb-6">
              <button
                onClick={() => setShowDialpad(!showDialpad)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-colors ${
                  showDialpad 
                    ? 'bg-teal-500 text-white' 
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <Grid3X3 className="w-5 h-5" />
                Dialpad
              </button>
              <button
                onClick={() => handleIncomingCall({
                  callerId: '+1 (555) 987-6543',
                  callerName: 'AI Fraud Detection',
                  reason: 'Suspicious Activity Transfer'
                })}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg font-medium transition-colors"
                data-testid="simulate-incoming-btn"
              >
                <PhoneIncoming className="w-5 h-5" />
                Simulate Incoming
              </button>
            </div>

            {/* Dialpad */}
            {showDialpad && (
              <div className="bg-[#1a1a1f] rounded-xl p-4">
                <input
                  type="tel"
                  value={dialNumber}
                  onChange={(e) => setDialNumber(e.target.value)}
                  placeholder="Enter phone number"
                  className="w-full bg-[#0d1117] border border-gray-700 rounded-lg px-4 py-3 text-white text-center text-xl font-mono mb-4 outline-none focus:border-teal-400"
                />
                <div className="grid grid-cols-3 gap-3 justify-items-center">
                  <DialButton digit="1" sub="" />
                  <DialButton digit="2" sub="ABC" />
                  <DialButton digit="3" sub="DEF" />
                  <DialButton digit="4" sub="GHI" />
                  <DialButton digit="5" sub="JKL" />
                  <DialButton digit="6" sub="MNO" />
                  <DialButton digit="7" sub="PQRS" />
                  <DialButton digit="8" sub="TUV" />
                  <DialButton digit="9" sub="WXYZ" />
                  <DialButton digit="*" sub="" />
                  <DialButton digit="0" sub="+" />
                  <DialButton digit="#" sub="" />
                </div>
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => setDialNumber(prev => prev.slice(0, -1))}
                    className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    ⌫ Delete
                  </button>
                  <button
                    onClick={makeCall}
                    disabled={!dialNumber}
                    className="flex-1 py-3 bg-green-500 hover:bg-green-600 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <Phone className="w-5 h-5" />
                    Call
                  </button>
                </div>
              </div>
            )}

            {/* Status when not dialing */}
            {!showDialpad && (
              <div className="text-center py-8">
                <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-teal-500/20 to-blue-500/20 border-2 border-teal-500/30 flex items-center justify-center mb-4">
                  <PhoneCall className="w-12 h-12 text-teal-400" />
                </div>
                <p className="text-gray-400">Waiting for incoming calls...</p>
                <p className="text-gray-500 text-sm mt-1">Calls transferred from AI agents will appear here</p>
              </div>
            )}
          </div>
        )}

        {/* Connecting State */}
        {callState === CALL_STATES.CONNECTING && (
          <div className="text-center py-8">
            <Loader2 className="w-12 h-12 text-teal-400 animate-spin mx-auto mb-4" />
            <p className="text-white font-medium">Connecting...</p>
            <p className="text-gray-400">{callerInfo?.number}</p>
          </div>
        )}

        {/* Call Ended State */}
        {callState === CALL_STATES.ENDED && (
          <div className="text-center py-8">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <p className="text-white font-medium">Call Ended</p>
            <p className="text-gray-400">Duration: {formatDuration(callDuration)}</p>
            <p className="text-teal-400 text-sm mt-2">Opening call details form...</p>
          </div>
        )}

        {/* Call Details Form State - Form is now shown permanently next to dialer */}
        {callState === CALL_STATES.FORM && (
          <div className="text-center py-8">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <p className="text-white font-medium">Call Completed</p>
            <p className="text-gray-400 text-sm mt-1">Please fill out the call details in the form</p>
            
            {/* DNC Result Message */}
            {dncResult && (
              <div className={`mt-4 p-3 rounded-lg text-sm ${
                dncResult.success 
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                  : 'bg-red-500/20 text-red-400 border border-red-500/30'
              }`}>
                {dncResult.message}
              </div>
            )}
            
            <div className="flex justify-center gap-3 mt-4">
              {/* Add to DNC Button - post call */}
              <button
                onClick={() => setShowDncConfirm(true)}
                className="px-4 py-2.5 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border border-orange-500/30 rounded-lg font-medium flex items-center gap-2 transition-colors"
                data-testid="dnc-post-call-btn"
              >
                <Ban className="w-4 h-4" />
                Add to DNC
              </button>
              
              <button
                onClick={closeForm}
                className="px-6 py-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium transition-colors"
                data-testid="ready-for-next-call-btn"
              >
                Ready for Next Call
              </button>
            </div>
          </div>
        )}

        {/* Transfer Modal */}
        {showTransfer && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 w-full max-w-sm mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">Transfer Call</h3>
                <button onClick={() => setShowTransfer(false)} className="text-gray-500 hover:text-gray-300">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <input
                type="tel"
                value={transferNumber}
                onChange={(e) => setTransferNumber(e.target.value)}
                placeholder="Enter transfer number"
                className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-3 text-white mb-4 outline-none focus:border-teal-400"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowTransfer(false)}
                  className="flex-1 py-2.5 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTransfer}
                  disabled={!transferNumber}
                  className="flex-1 py-2.5 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                >
                  Transfer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Recent Calls */}
        {recentCalls.length > 0 && callState === CALL_STATES.IDLE && !showDialpad && (
          <div className="mt-6 pt-6 border-t border-gray-800">
            <h3 className="text-gray-400 text-sm font-medium mb-3">Recent Calls</h3>
            <div className="space-y-2">
              {recentCalls.slice(0, 5).map((call) => (
                <div key={call.id} className="flex items-center justify-between p-3 bg-[#1a1a1f] rounded-lg">
                  <div className="flex items-center gap-3">
                    <PhoneIncoming className="w-4 h-4 text-green-400" />
                    <div>
                      <p className="text-white text-sm">{call.name}</p>
                      <p className="text-gray-500 text-xs">{call.number}</p>
                    </div>
                  </div>
                  <span className="text-gray-400 text-sm">{formatDuration(call.duration)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* DNC Confirmation Modal */}
      {showDncConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-[#1a1a1f] border border-gray-800 rounded-2xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Ban className="w-6 h-6 text-orange-400" />
              </div>
              <h3 className="text-xl font-bold text-white">Add to DNC List?</h3>
            </div>
            
            <p className="text-gray-400 mb-4">
              Are you sure you want to add this number to the Do Not Call list?
            </p>
            
            <div className="bg-[#0d1117] rounded-lg p-3 mb-6">
              <p className="text-white font-mono text-lg text-center">
                {callerInfo?.phone || currentCall?.from || dialedNumber || 'Unknown'}
              </p>
              {callerInfo?.name && (
                <p className="text-gray-400 text-sm text-center mt-1">{callerInfo.name}</p>
              )}
            </div>
            
            <p className="text-gray-500 text-xs mb-4">
              This number will be blocked from all future campaign calls.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDncConfirm(false)}
                disabled={addingToDnc}
                className="flex-1 px-4 py-2.5 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddToDnc}
                disabled={addingToDnc}
                className="flex-1 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                data-testid="confirm-dnc-add-btn"
              >
                {addingToDnc ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Ban className="w-4 h-4" />
                    Block Number
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentDialer;
